import React, { useState } from "react";
import { FiUploadCloud, FiX } from "react-icons/fi";
import Button from "../../../../components/ACS-Dashboard/ACS-Blocks/Button";
import notify from "../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import { bulkUpdateApi } from "./bulkUpdateApi";
import MapColumnsStep from "./MapColumnsStep";
import DryRunReviewStep from "./DryRunReviewStep";
import ExecuteResultsStep from "./ExecuteResultsStep";

// Only Location is wired up today, but the wizard/API contract underneath is entity-agnostic
// (see backend bulkupload.service.EntityUploadHandler) -- adding User/Organization later only
// needs a new "entityType" value here plus a new backend handler, not a new wizard.
const STEP_LABELS = ["Upload", "Map Columns", "Review Changes", "Apply & Confirm"];

const BulkUpdateLocationsTab = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [entityType] = useState("LOCATION");
  const [loading, setLoading] = useState(false);

  const [file, setFile] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [sheetName, setSheetName] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [sampleRows, setSampleRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [fieldCatalog, setFieldCatalog] = useState([]);

  const [mappings, setMappings] = useState([]); // [{sourceColumn, targetField, role, apply}]
  const [dryRunResult, setDryRunResult] = useState(null);
  const [rowDecisions, setRowDecisions] = useState({}); // rowIndex -> {include, resolvedRecordId}
  const [executeResult, setExecuteResult] = useState(null);

  const runPreview = async (selectedFile, selectedSheet) => {
    setLoading(true);
    try {
      const preview = await bulkUpdateApi.preview(entityType, selectedFile, selectedSheet);
      setSheetNames(preview.sheetNames || []);
      setSheetName(preview.activeSheetName);
      setHeaders(preview.headers || []);
      setSampleRows(preview.sampleRows || []);
      setTotalRows(preview.totalRows || 0);
      setFieldCatalog(preview.fieldCatalog || []);
      setMappings(
        (preview.headers || []).map((header) => {
          const targetField = preview.suggestedMapping?.[header] || null;
          const descriptor = (preview.fieldCatalog || []).find((f) => f.key === targetField);
          return {
            sourceColumn: header,
            targetField,
            role: descriptor ? (descriptor.matchable ? "MATCH" : "UPDATE") : null,
            apply: true,
          };
        })
      );
    } catch (err) {
      notify.error(err?.response?.data?.message || "Could not read that file. Check the format and try again.");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelected = async (selectedFile) => {
    if (!selectedFile) return;
    setFile(selectedFile);
    try {
      await runPreview(selectedFile, null);
    } catch {
      setFile(null);
    }
  };

  const handleSheetChange = async (newSheetName) => {
    setSheetName(newSheetName);
    await runPreview(file, newSheetName);
  };

  const handleRunDryRun = async () => {
    setLoading(true);
    try {
      const result = await bulkUpdateApi.dryRun(entityType, file, sheetName, mappings);
      setDryRunResult(result);
      const initialDecisions = {};
      result.rows.forEach((row) => {
        initialDecisions[row.rowIndex] = {
          include: row.status === "MATCHED" || row.status === "AMBIGUOUS",
          resolvedRecordId: null,
        };
      });
      setRowDecisions(initialDecisions);
      setStep(3);
    } catch (err) {
      notify.error(err?.response?.data?.message || "Dry run failed. Check your column mapping and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    const decisions = Object.entries(rowDecisions)
      .filter(([, d]) => d.include)
      .map(([rowIndex, d]) => ({
        rowIndex: Number(rowIndex),
        include: true,
        resolvedRecordId: d.resolvedRecordId || null,
      }));
    if (decisions.length === 0) {
      notify.error("Select at least one row to apply.");
      return;
    }
    setLoading(true);
    try {
      const result = await bulkUpdateApi.execute(entityType, file, sheetName, mappings, decisions);
      setExecuteResult(result);
      setStep(4);
      notify.success(`Updated ${result.updatedCount} location(s).`);
    } catch (err) {
      notify.error(err?.response?.data?.message || "Applying changes failed.");
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    setStep(1);
    setFile(null);
    setSheetNames([]);
    setSheetName(null);
    setHeaders([]);
    setSampleRows([]);
    setMappings([]);
    setDryRunResult(null);
    setRowDecisions({});
    setExecuteResult(null);
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between px-fluid-1 pt-fluid-1">
        <button
          type="button"
          onClick={onClose}
          className="text-fluid-sm text-acs-primary-navy dark:text-acs-accent font-semibold hover:underline"
        >
          &larr; Back to Locations
        </button>
        <div className="flex items-center gap-fluid-2">
          {STEP_LABELS.map((label, i) => {
            const idx = i + 1;
            const active = idx === step;
            const done = idx < step;
            return (
              <div key={label} className="flex items-center gap-fluid-1">
                <span
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-fluid-xs font-semibold
                    ${active ? "bg-acs-primary-navy dark:bg-acs-primary-navy-dark text-white" : ""}
                    ${done ? "bg-acs-accent text-white" : ""}
                    ${!active && !done ? "bg-gray-200 dark:bg-acs-dark-border text-gray-500 dark:text-acs-dark-subtext" : ""}
                  `}
                >
                  {done ? "✓" : idx}
                </span>
                <span
                  className={`text-fluid-sm ${
                    active ? "font-semibold text-acs-primary-navy dark:text-acs-dark-text" : "text-gray-500 dark:text-acs-dark-subtext"
                  }`}
                >
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-fluid-2">
        {step === 1 && (
          <UploadStep
            file={file}
            sheetNames={sheetNames}
            sheetName={sheetName}
            totalRows={totalRows}
            headers={headers}
            loading={loading}
            onFileSelected={handleFileSelected}
            onSheetChange={handleSheetChange}
            onClear={resetAll}
            onContinue={() => setStep(2)}
          />
        )}
        {step === 2 && (
          <MapColumnsStep
            headers={headers}
            sampleRows={sampleRows}
            fieldCatalog={fieldCatalog}
            mappings={mappings}
            setMappings={setMappings}
            loading={loading}
            onBack={() => setStep(1)}
            onContinue={handleRunDryRun}
          />
        )}
        {step === 3 && dryRunResult && (
          <DryRunReviewStep
            dryRunResult={dryRunResult}
            rowDecisions={rowDecisions}
            setRowDecisions={setRowDecisions}
            loading={loading}
            onBack={() => setStep(2)}
            onApply={handleExecute}
          />
        )}
        {step === 4 && executeResult && (
          <ExecuteResultsStep executeResult={executeResult} onDone={onClose} onStartOver={resetAll} />
        )}
      </div>
    </div>
  );
};

const UploadStep = ({
  file,
  sheetNames,
  sheetName,
  totalRows,
  headers,
  loading,
  onFileSelected,
  onSheetChange,
  onClear,
  onContinue,
}) => {
  const [dragOver, setDragOver] = useState(false);

  return (
    <div className="max-w-2xl mx-auto flex flex-col gap-fluid-3">
      {!file && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const dropped = e.dataTransfer.files?.[0];
            if (dropped) onFileSelected(dropped);
          }}
          className={`border-2 border-dashed rounded-xl p-fluid-4 flex flex-col items-center justify-center gap-fluid-2 text-center
            ${dragOver ? "border-acs-primary-navy bg-custom-bg-light-purple dark:bg-acs-dark-surface" : "border-gray-300 dark:border-acs-dark-border"}
          `}
        >
          <FiUploadCloud size={36} className="text-acs-primary-navy dark:text-acs-accent" />
          <p className="text-fluid-sm text-gray-600 dark:text-acs-dark-subtext">
            Drag and drop a spreadsheet (.xlsx, .xls, .csv), or
          </p>
          <label className="cursor-pointer">
            <span className="text-fluid-sm font-semibold text-acs-primary-navy dark:text-acs-accent hover:underline">
              browse files
            </span>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && onFileSelected(e.target.files[0])}
            />
          </label>
          {loading && <p className="text-fluid-xs text-gray-500 dark:text-acs-dark-subtext">Reading file...</p>}
        </div>
      )}

      {file && (
        <div className="border border-gray-200 dark:border-acs-dark-border rounded-lg p-fluid-2 flex flex-col gap-fluid-2 bg-white dark:bg-acs-dark-surface">
          <div className="flex items-center justify-between">
            <span className="text-fluid-sm font-medium text-gray-800 dark:text-acs-dark-text">{file.name}</span>
            <button type="button" onClick={onClear} className="text-gray-400 hover:text-gray-600 dark:text-acs-dark-subtext">
              <FiX size={18} />
            </button>
          </div>

          {sheetNames.length > 1 && (
            <div className="flex items-center gap-fluid-2">
              <label className="text-fluid-sm text-gray-600 dark:text-acs-dark-subtext">Sheet:</label>
              <select
                value={sheetName || ""}
                onChange={(e) => onSheetChange(e.target.value)}
                className="border border-gray-300 dark:border-acs-dark-border rounded-md px-2 py-1 text-fluid-sm
                           bg-white dark:bg-acs-dark-bg text-gray-800 dark:text-acs-dark-text"
              >
                {sheetNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {headers.length > 0 && (
            <p className="text-fluid-sm text-gray-600 dark:text-acs-dark-subtext">
              {totalRows} rows found across {headers.length} columns.
            </p>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <Button
          text="Continue to Column Mapping"
          onClick={onContinue}
          disabled={!file || headers.length === 0 || loading}
          className="!w-auto whitespace-nowrap !px-fluid-4"
        />
      </div>
    </div>
  );
};

export default BulkUpdateLocationsTab;
